/**
 * Vue Store related to current user information.
 *
 * // Note: Copy-pasted from flow-website-static
 *
 * @author Documill 2024
 */

import apiCall from '../../utils/api';
import {logger} from '../../utils/logger';

export default {

  // TODO: Consider if we should add a namespace for this state.
  // TODO: Why these are empty strings and not e.g. null?

  state: {
    userId: '',
    email: '',
    /** User's private Documents. */
    userDocuments: [],
    userRole: '',
    hasUserAvatar: false,
    userOrganizationId: '',
    userLocale: '',
    fullName: '',
    guestCollaboratorId: '',
    organization: '',
    assignedGroups: [],
    privateProjectsAllowed: false,
    userTimezone: '',
    appOnlyUser: false,
    avatarURL: '',
  },

  getters: {

    getUserId: state => state.userId,
    getUserDocuments: state => state.userDocuments,
    getUserEmail: state => state.email,
    getUserFullName: state => state.fullName,
    getUserLocale: state => state.userLocale,
    getUserRole: state => state.userRole,
    hasUserAvatar: state => state.hasUserAvatar,
    getUserDocumentById: (state) => userDocumentId => {
      return state.userDocuments.find(document => document.id === userDocumentId);
    },
    getUserOrganizationId: state => state.userOrganizationId,
    getUserOrganizationName: state => state.organization.name,
    getUserOrganizationUrlName: state => state.organization.urlName,
    isUserOrganizationMyFiles: state => state.organization.myFiles,
    /**
     * Gets current user organization brand (colors, logo, email footer info), or null if the
     * organization has no custom brand configured.
     *
     * How to use:
     * this.$store.getters.getOrganizationBrand
     */
    getOrganizationBrand: state => state.organization.brand,
    getUserIdentityProviderId: state => state.identityProviderId,
    getGuestCollaboratorId: state => state.guestCollaboratorId,
    getAvatarURL: state => state.avatarURL,
    /**
     * Tests whether the storages have been enabled in Organization. What this
     * means that there is at least one (1) storage integration that exists for
     * this Organization where the Project files can be stored.
     *
     * How to use:
     * this.$store.getters.isStoragesEnabled
     */
    isStoragesEnabled: state => state.organization.storagesEnabled,
    isSubscriptionExpired: state => state.organization.subscriptionExpired,
    isExternalTeamsFeatureEnabled: state => state.organization.externalTeamsFeatureEnabled,
    getAssignedUserGroups: state => state.assignedGroups,
    isPrivateProjectsAllowed: state => state.privateProjectsAllowed,
    getUserTimezone: state => state.userTimezone,
    isAppOnlyUser: state => state.appOnlyUser,
    getUserInfoState : state => state,
  },

  mutations: {

    /**
     * Sets user data to state.
     *
     * Expected parameters:
     *
     * userInfo: {
     *  id: String,
     *  email: String,
     *  role: String,
     *  hasAvatar: Boolean,
     *  locale: String,
     *  organizationId: String,
     *  fullName: String,
     * }
     */
    SET_USER_INFO(state, userInfo) {
      state.userId = userInfo.id;
      state.email = userInfo.email;
      state.userRole = userInfo.role;
      state.hasUserAvatar = userInfo.hasAvatar;
      state.userOrganizationId = userInfo.organizationId;
      state.userLocale = userInfo.locale;
      state.fullName = userInfo.fullName;
      state.organization = userInfo.organization;
      state.guestCollaboratorId = userInfo.guestCollaboratorId;
      state.identityProviderId = userInfo.identityProviderId;
      state.assignedGroups = userInfo.assignedGroups;
      state.privateProjectsAllowed = userInfo.privateProjectsAllowed;
      state.userTimezone = userInfo.timezone;
      state.appOnlyUser = userInfo.appOnlyUser;
    },

    /**
     * Resets the user information from state.
     */
    RESET_USER_INFO(state) {
      state.userId = '';
      state.email = '';
      state.userDocuments = [];
      state.userRole = '';
      state.hasUserAvatar = false;
      state.userOrganizationId = '';
      state.userLocale = '';
      state.fullName = '';
      state.guestCollaboratorId = '';
      state.organization= '';
      state.identityProviderId= '';
      state.userTimezone = '';
      state.avatarURL='';
    },

    SET_USER_LOCALE(state, locale) {
      state.userLocale = locale;
    },

    /**
     * Sets user name to state.
     */
    SET_USER_NAME(state, name) {
      state.fullName = name;
    },

    SET_USER_AVATAR(state, avatar) {
      state.hasUserAvatar = !!avatar;
    },

    SET_USER_ORGANIZATION(state,organization){
      state.organization = organization;
    },

    SET_USER_ORGANIZATION_STORAGES_ENABLED(state,enabled){
      state.organization.storagesEnabled = enabled;
    },

    SET_PRIVATE_PROJECTS_ALLOWED(state,enabled){
      state.privateProjectsAllowed = enabled;
    },

    SET_USER_TIMEZONE(state, timezone) {
      state.userTimezone = timezone;
    },

    SET_AVATAR_URL(state, newValue) {
      state.avatarURL = newValue;
    }
  },

  actions: {

    /**
     * Gets user information from back-end by calling 'whoami' API.
     *
     * Also sets the user information data to state.
     */
    getUserInfo(context) {

      return new Promise((resolve, reject) => {
        apiCall.get('/v1/users/data')
          .then(result => {
            const userData = result.data;

            let userRole = userData.organizationUserRole;
            let organizationId = userData.organization.id;
            let identityProviderId = userData.identityProviderId;

            let data = {
              email: userData.email,
              role: userRole,
              organizationId: organizationId,
              id: userData.id,
              fullName: userData.fullName,
              hasAvatar: userData.hasAvatar,
              organization: userData.organization,
              guestCollaboratorId: userData.guestCollaboratorId,
              identityProviderId: identityProviderId,
              locale: userData.locale,
              assignedGroups: userData.assignedGroups,
              privateProjectsAllowed: userData.privateProjectsAllowed,
              timezone: userData.timezone,
              appOnlyUser: userData.appOnlyUser
            };

            context.commit("SET_USER_INFO", data);
            context.commit("ORGANIZATION_SIGN_IN", organizationId);

            if(userData.id)
              context.commit("SET_AVATAR_URL",window.FlowSignOnlyWebsiteGlobalConfig.basePaths.flowController +
                                              "/v1/users/" + userData.id+ "/avatar")

            logger.debug("Query getUser succeeded with result:", userData);
            resolve({result: "found_user", data: data});
          })
          .catch(error => {
            if(error.response && error.response.status != 401) {
              // Unauthorized; Consider this a success with result "unauthorized"
              // Reject anyway, but make error object available for getting status
              logger.error("Query getUser didn't succeed with message:", error);
            }
            reject(error);
          });
      });
    },

    /**
     * Clears user data from local storage and state. Also clears all user
     * rights.
     */
    clearUserInfo(context) {
      context.commit("RESET_USER_INFO");
    },
  }
};
